<template>
  <template v-if="showPropertiesBlock">
    <VcCollapsibleContent max-height="30rem">
      <InnoProperty :key="props.product.code" :label="$t('common.labels.sku_long')" class="text-base bg-neutral-100">
        {{ props.product.code }}
      </InnoProperty>

      <InnoProperty
        v-for="(property, index) in properties"
        :key="property.name"
        :label="property.label!"
        class="text-base"
        :class="index % 2 != 0 ? 'bg-neutral-100' : ''"
      >
        <!-- Go to brand page if property is merk-->

        <a
          class="underline text-primary-500"
          v-if="featuredBrand && INNO_BRAND_PROPERTY === property.name"
          :href="`/${featuredBrand.permalink}`"
          >{{ property.value }}</a
        >

        <a
          class="underline text-primary-500"
          v-else-if="INNO_BRAND_PROPERTY === property.name"
          :href="`/merken/${(property.value as string).toLowerCase()}`"
          >{{ property.value }}</a
        >
        <template v-else>{{ property.value }}</template>
      </InnoProperty>

      <!-- Vendor -->
      <InnoProperty v-if="showVendor" :label="$t('shared.catalog.product_details.vendor_label')" class="text-base">
        <Vendor :vendor="product.vendor!" with-rating />
      </InnoProperty>
    </VcCollapsibleContent>
    <!-- Properties -->
  </template>
</template>

<script setup lang="ts">
import { computed, inject } from "vue";
import { PropertyType } from "@/core/api/graphql/types";
import { configInjectionKey } from "@/core/injection-keys";
import { getPropertiesGroupedByName } from "@/core/utilities";
import { Vendor } from "@/shared/catalog";
import type { Product } from "@/core/api/graphql/types";
import { INNO_BRAND_PROPERTY } from "@/core/constants";
import { useBrands } from "@/shared/catalog";

interface IProps {
  product: Product;
  model: {
    title?: string;
    hidden?: boolean;
  };
}

const props = defineProps<IProps>();
const config = inject(configInjectionKey, {});
const notAvailableProperties = config?.inactive_properties_on_product_card;

const properties = computed(() =>
  Object.values(
    getPropertiesGroupedByName(
      props.product?.properties?.filter((prop) => !notAvailableProperties?.includes(prop.name)) ?? [],
      PropertyType.Product,
    ),
  ),
);

const brandName = computed(() => properties.value.find((item) => item.name === INNO_BRAND_PROPERTY)?.value as string);

const { brands } = useBrands({
  keyword: brandName.value ?? "",
  itemsPerPage: 1,
});

const featuredBrand = computed(() => brands.value[0]);

const showVendor = computed(() => config.vendor_enabled && !props.product.hasVariations && props.product.vendor);
const showPropertiesBlock = computed(() => !props.model.hidden && (properties.value.length || showVendor.value));
</script>
